import fp from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { DayPickerProps, useDayPicker, useNavigation } from 'react-day-picker-for-new-calendar';
import { addMonths } from 'date-fns';
import { NativeSelect } from '@mui/material';

import { ArrowsUpdown24 } from '../../../icons';
import { Typography } from '../../Typography';
import { Box } from '../../Box';


type CaptionOption = {
    value: string | number;
    label: string | number;
    disabled?: boolean;
};

export const CaptionLabel: DayPickerProps['components']['CaptionLabel'] = ({
    displayMonth,
}) => {
    const { locale } = useDayPicker();
    const { goToMonth } = useNavigation();
    return (
        <Box display='flex' justifyContent='center' px={1} mb={2}>
            <Box position='relative'>
                <NativeSelect
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        fontSize: '14px',
                        zIndex: 1,

                        '&.Mui-focused + div > div:first-of-type': {
                            borderColor: 'grayscale.shade20',
                        },
                    }}
                    value={`${displayMonth.getFullYear()}-${displayMonth.getMonth()}`}
                    onChange={useCallback(
                        e => {
                            const target = e.target as HTMLSelectElement;
                            const [year, month] = target.value.split('-');
                            goToMonth(new Date(+year, +month));
                        },
                        [goToMonth],
                    )}
                >
                    {useMemo(
                        () =>
                            fp.pipe(
                                fp.range(-12),
                                fp.reduce<number, CaptionOption[]>((r, n) => {
                                    const newDate = addMonths(displayMonth, n);
                                    const newDateYear = newDate.getFullYear();
                                    const result = [...r];
                                    if (!r.some(o => o.value === newDateYear)) {
                                        result.push({
                                            value: newDateYear,
                                            label: newDateYear,
                                            disabled: true,
                                        });
                                    }
                                    result.push({
                                        value: `${newDateYear}-${newDate.getMonth()}`,
                                        label: fp.upperFirst(
                                            newDate.toLocaleDateString(
                                                locale.code,
                                                { month: 'long' },
                                            ),
                                        ),
                                    });
                                    return result;
                                }, []),
                            )(13),
                        [displayMonth, locale],
                    ).map(c => (
                        <option
                            key={c.value}
                            value={c.value}
                            disabled={c.disabled}
                        >
                            {c.label}
                        </option>
                    ))}
                </NativeSelect>
                <Box
                    position='relative'
                    display='flex'
                    alignItems='center'
                    px={2}
                    border={4}
                    borderColor='transparent'
                    borderRadius={2}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -4,
                            right: 0,
                            bottom: -4,
                            left: 0,
                            border: 2,
                            borderColor: 'transparent',
                            borderRadius: 2,
                        }}
                    />
                    <Typography
                        variant='boldCaption1'
                        color='grayscale.shade95'
                    >
                        {`${fp.upperFirst(
                            displayMonth.toLocaleDateString(locale.code, {
                                month: 'long',
                            }),
                        )} ${displayMonth.getFullYear()}`}
                    </Typography>
                    <ArrowsUpdown24
                        sx={({ spacing }) => ({
                            width: spacing(4),
                            height: spacing(4),
                            color: 'grayscale.shade95',
                            ml: 1,
                        })}
                    />
                </Box>
            </Box>
        </Box>
    );
};
